import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * Tiempo (ms) que una operación debe estar en vuelo antes de pintar el
 * loader. Bajo este umbral no aparece — evita el "flash" en queries rápidas
 * que terminan en pocos cientos de ms y darían sensación de parpadeo.
 *
 * Subido de 400ms → 3000ms en sprint 2026-06-16: el threshold previo, aun
 * siendo el sweet spot teórico Material Design, disparaba el overlay
 * demasiado a menudo en navegación normal (mutations optimistas, refetches
 * cortos, geo lookups), y el "tirando los dados" intermitente molestaba.
 * 3s deja el overlay reservado para operaciones genuinamente lentas
 * (cold load contra BGG API, primer fetch sin cache en red mala). Las
 * mutations típicas terminan antes del umbral y el user no ve nada.
 */
const DELAY_MS = 3000

/**
 * Loader global "tirando los dados" — overlay con dado D6 brutalist
 * rodando que aparece cuando hay una mutation en vuelo o una query
 * cargando por primera vez (sin data cacheada) durante más de
 * {@link DELAY_MS}.
 *
 * <p>Se monta UNA SOLA VEZ en el árbol (dentro del QueryProvider para tener
 * acceso al QueryClient via context). No requiere instrumentar cada
 * llamada individual — se suscribe al estado global de TanStack Query
 * vía {@link useIsFetching}/{@link useIsMutating}.</p>
 *
 * <p><b>Qué cuenta como "carga"</b>:</p>
 * <ul>
 *   <li>Cualquier mutation en vuelo (siempre — son acciones del usuario).</li>
 *   <li>Queries que están haciendo fetch SIN data cacheada previa (carga
 *       inicial). Se excluyen background refetches porque el usuario ya
 *       tiene contenido visible y el refetch es invisible — no merece
 *       ocultar la pantalla.</li>
 * </ul>
 *
 * <p>Accesibilidad: el overlay lleva {@code role="status"} +
 * {@code aria-live="polite"} para que lectores de pantalla anuncien la
 * carga. Si el usuario tiene {@code prefers-reduced-motion}, el dado se
 * queda quieto (la animación está en {@code @keyframes diceRoll} de
 * Tailwind, que la regla global de globals.css ya anula con esa media
 * query).</p>
 */
export function GlobalDiceLoader() {
  const { t } = useTranslation()
  const fetchingCount = useIsFetching({
    // Solo carga inicial — la query aún no tiene datos. Excluye background
    // refetches (refetchOnMount cuando hay cache, polling de unread-count,
    // etc.) que no deben bloquear la UI.
    predicate: (q) => q.state.data === undefined,
  })
  const mutatingCount = useIsMutating()
  const isBusy = fetchingCount > 0 || mutatingCount > 0

  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isBusy) {
      setVisible(false)
      return
    }
    const timer = window.setTimeout(() => setVisible(true), DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [isBusy])

  if (!visible) return null

  // createPortal asegura que el overlay se monta al final del body, fuera
  // de cualquier transform/overflow ancestor que rompería el fixed.
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-label={t('common.loadingDice')}
      className="bg-background/92 fixed inset-0 z-[100] flex flex-col items-center justify-center backdrop-blur-[2px]"
    >
      {/* Dado D6 brutalist — cara 5 (5 pips) con el meeple rodando */}
      <div className="brutal-lg flex size-28 origin-center animate-dice-roll items-center justify-center rounded-2xl bg-red">
        <div className="grid size-16 grid-cols-3 grid-rows-3 gap-1" aria-hidden="true">
          <span className="rounded-full bg-background" />
          <span />
          <span className="rounded-full bg-background" />
          <span />
          <span className="rounded-full bg-background" />
          <span />
          <span className="rounded-full bg-background" />
          <span />
          <span className="rounded-full bg-background" />
        </div>
      </div>
      <p className="mt-8 text-balance font-display text-2xl font-black uppercase">
        {t('common.loadingDice')}
      </p>
      <p className="mt-2 font-brutal text-xs uppercase tracking-widest text-muted-foreground">
        {t('common.loadingHint')}
      </p>
    </div>,
    document.body,
  )
}
